import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import { setLoading } from '../../../store/actions';
import { getPropertyList } from '../../Inventory/pages/InventoryOrders/utils/api';

import {
  getVoiceAssistantConfigs,
  deleteVoiceAssistantConfig,
  getVoiceAnalytics,
  getVoiceQuota,
} from './Components/FormikHelpers/Api';

import { VoiceAssistantConfigModal } from './Components/VoiceAssistantConfigModal';
import { DeleteVoiceAssistantModal } from './Components/DeleteVoiceAssistantModal';
import { VoiceLeadsTable } from './Components/VoiceLeadsTable';
import { VoiceSessionsTable } from './Components/VoiceSessionsTable';
import { formatDuration, formatCost } from './Components/FormikHelpers/InitialValues';

const ConfigCard = ({ cfg, onEdit, onDelete }) => {
  const [copied, setCopied] = useState(false);

  const copyToken = () => {
    navigator.clipboard?.writeText(cfg.token || '');
    setCopied(true);
    toast.success('Token copied');
    setTimeout(() => setCopied(false), 1500);
  };

  const tagStyle = (bg, color, border) => ({
    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
    background: bg, color, border: `1px solid ${border}`,
    display: 'inline-block',
  });

  return (
    <div
      className="va-config-card"
      style={{
        background: '#fff', border: '1px solid #E2E8F0',
        borderRadius: 12, overflow: 'hidden',
        boxShadow: '0 2px 10px rgba(15,28,46,0.06)',
        transition: 'box-shadow .15s, border-color .15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#BFDBFE'; e.currentTarget.style.boxShadow = '0 4px 18px rgba(29,78,216,0.10)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(15,28,46,0.06)'; }}
    >
      <div style={{ height: 3, background: cfg.is_active ? 'linear-gradient(90deg, #1D4ED8, #059669)' : '#CBD5E1' }} />

      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: '#EFF6FF', border: '1px solid #BFDBFE',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>🎙</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                {cfg.persona?.assistant_name || 'Assistant'}
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2, fontFamily: 'monospace' }}>
                /{cfg.voice_slug}
              </div>
            </div>
          </div>
          <span style={{
            ...tagStyle(
              cfg.is_active ? '#ECFDF5' : '#FEF2F2',
              cfg.is_active ? '#059669' : '#DC2626',
              cfg.is_active ? '#6EE7B7' : '#FECACA'
            ),
          }}>
            {cfg.is_active ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </div>

        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FeatherIcon icon="phone" size={11} /> {cfg.integrations?.connect_phone_number || 'No phone configured'}
        </div>
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FeatherIcon icon="cloud" size={11} />
          {cfg.integrations?.aws_region || 'us-east-1'}
          {cfg.integrations?.connect_instance_id && (
            <span style={{ color: '#CBD5E1' }}>· {cfg.integrations.connect_instance_id.slice(0, 8)}…</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FeatherIcon icon="cpu" size={11} />
          {cfg.ai?.model?.replace('anthropic.', '').replace(/-v\d+:\d+$/, '') || 'claude-haiku'}
          <span style={{ color: '#CBD5E1' }}>·</span>
          Polly TTS
          <span style={{ color: '#CBD5E1' }}>·</span>
          Transcribe STT
        </div>

        <div style={{
          background: '#F8FAFC', border: '1px solid #E2E8F0',
          borderRadius: 7, padding: '6px 10px',
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
        }}>
          <code style={{ fontSize: 10, color: '#94A3B8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cfg.token}
          </code>
          <button
            type="button"
            onClick={copyToken}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#059669' : '#94A3B8', fontSize: 13, padding: '2px 4px', flexShrink: 0 }}
            title="Copy token"
          >
            <FeatherIcon icon={copied ? 'check' : 'copy'} size={12} />
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
          {cfg.lead_config?.auto_collect && (
            <span style={tagStyle('#EFF6FF', '#1D4ED8', '#BFDBFE')}>Lead Capture</span>
          )}
          {cfg.call_limits?.business_hours_only && (
            <span style={tagStyle('#FFFBEB', '#D97706', '#FDE68A')}>Business Hours</span>
          )}
          {cfg.integrations?.crm_webhook && (
            <span style={tagStyle('#ECFDF5', '#059669', '#6EE7B7')}>CRM Webhook</span>
          )}
          {cfg.integrations?.booking_url && (
            <span style={tagStyle('#F5F3FF', '#7C3AED', '#DDD6FE')}>Booking URL</span>
          )}
          {cfg.integrations?.connect_instance_id && (
            <span style={tagStyle('#F0FDF4', '#15803D', '#BBF7D0')}>AWS Connect</span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="ri-btn ri-btn--primary"
            style={{ flex: 1 }}
            onClick={() => onEdit(cfg)}
          >
            <FeatherIcon icon="edit-2" size={12} /> Edit
          </button>
          <button
            type="button"
            onClick={() => onDelete(cfg)}
            style={{
              background: 'transparent', border: '1px solid #FECACA',
              borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
              color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            title="Delete"
          >
            <FeatherIcon icon="trash-2" size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

const VoiceAssistant = () => {
  const dispatch = useDispatch();

  const [configs, setConfigs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [quota, setQuota] = useState(null);
  const [properties, setProperties] = useState([]);
  const [activeTab, setActiveTab] = useState('configs');
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [configModal, setConfigModal] = useState({ open: false, mode: 'add', config: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, config: null });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchConfigs = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const res = await getVoiceAssistantConfigs();
      setConfigs(res?.data || []);
    } catch {
      toast.error('Failed to load voice assistant configs');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [analyticsRes, quotaRes] = await Promise.all([getVoiceAnalytics(), getVoiceQuota()]);
      setAnalytics(analyticsRes?.data || null);
      setQuota(quotaRes?.data || null);
    } catch { }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    try {
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch {
      Swal.fire('Error', 'Failed to load properties', 'error');
    }
  }, [getUserFromToken]);

  useEffect(() => {
    fetchProperties();
    fetchConfigs();
    fetchAnalytics();
  }, [fetchProperties, fetchConfigs, fetchAnalytics]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.config?._id) return;
    try {
      dispatch(setLoading(true));
      await deleteVoiceAssistantConfig(deleteModal.config._id);
      toast.success('Voice assistant deleted');
      setRefreshKey((k) => k + 1);
      fetchConfigs();
    } finally {
      dispatch(setLoading(false));
      setDeleteModal({ open: false, config: null });
    }
  }, [dispatch, deleteModal.config, fetchConfigs]);

  const handleSaved = useCallback(() => {
    fetchConfigs();
    fetchAnalytics();
    setRefreshKey((k) => k + 1);
  }, [fetchConfigs, fetchAnalytics]);

  const statCards = analytics ? [
    { icon: 'users', label: 'Total Leads', value: analytics.total_leads, color: '#1D4ED8' },
    { icon: 'bell', label: 'New Leads', value: analytics.new_leads, color: '#DC2626' },
    { icon: 'phone-call', label: 'Total Calls', value: analytics.total_sessions, color: '#0D9488' },
    { icon: 'trending-up', label: 'Conversion', value: analytics.conversion_rate, color: '#7C3AED' },
    { icon: 'dollar-sign', label: 'Est. Cost (30d)', value: formatCost(analytics.period_totals?.estimated_cost_usd), color: '#D97706' },
  ] : [];

  const tabStyle = (key) => ({
    fontFamily: 'inherit',
    padding: '9px 18px',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    borderBottom: activeTab === key ? '2px solid #1D4ED8' : '2px solid transparent',
    background: 'transparent',
    color: activeTab === key ? '#1D4ED8' : '#64748B',
    transition: 'all 0.12s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    position: 'relative',
  });

  return (
    <div className="page-content">
      <MetaTags><title>Rentdigicare | Voice Assistant</title></MetaTags>

      <div className="container-fluid mb-2">
        <Breadcrumbs title="Settings" breadcrumbItem="Voice Assistant" />

        {statCards.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            {statCards.map(({ icon, label, value, color }) => (
              <div
                key={label}
                style={{
                  background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10,
                  padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
                  boxShadow: '0 2px 8px rgba(15,28,46,0.05)',
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 9, background: `${color}12`, border: `1px solid ${color}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FeatherIcon icon={icon} size={16} color={color} />
                </div>
                <div>
                  <div style={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</div>
                </div>
              </div>
            ))}

            {quota && (
              <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: '14px 16px', boxShadow: '0 2px 8px rgba(15,28,46,0.05)' }}>
                <div style={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Today's Quota</div>
                <div style={{ background: '#F1F5F9', borderRadius: 4, height: 6, marginBottom: 4 }}>
                  <div style={{
                    height: '100%', borderRadius: 4,
                    width: `${Math.min(quota.usage_pct || 0, 100)}%`,
                    background: (quota.usage_pct || 0) >= 80 ? '#DC2626' : (quota.usage_pct || 0) >= 60 ? '#D97706' : '#1D4ED8',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ fontSize: 11, color: '#64748B' }}>
                  {quota.today_usage?.calls ?? 0} / {quota.limits?.max_calls_per_day ?? '∞'} calls
                  <span style={{ marginLeft: 8, color: '#0D9488', fontWeight: 600 }}>{quota.active_calls} active</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, boxShadow: '0 4px 24px rgba(15,28,46,0.08)' }}>
          <div style={{ borderBottom: '1px solid #E2E8F0', display: 'flex', padding: '0 20px', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex' }}>
              {[
                { key: 'configs', label: 'Assistants', icon: 'cpu', badge: null },
                { key: 'leads', label: 'Leads', icon: 'user', badge: analytics?.new_leads > 0 ? analytics.new_leads : null },
                { key: 'sessions', label: 'Sessions', icon: 'phone', badge: null },
              ].map(({ key, label, icon, badge }) => (
                <button key={key} type="button" style={tabStyle(key)} onClick={() => setActiveTab(key)}>
                  <FeatherIcon icon={icon} size={13} />
                  {label}
                  {badge && (
                    <span style={{
                      background: '#DC2626', color: '#fff', fontSize: 9, fontWeight: 800,
                      borderRadius: 99, padding: '1px 6px', minWidth: 18, textAlign: 'center',
                    }}>{badge}</span>
                  )}
                </button>
              ))}
            </div>

            {activeTab === 'configs' && (
              <button
                type="button"
                className="ri-btn ri-btn--primary"
                onClick={() => setConfigModal({ open: true, mode: 'add', config: null })}
              >
                <FeatherIcon icon="plus" size={13} /> New Assistant
              </button>
            )}
          </div>

          <div style={{ padding: activeTab === 'configs' ? 20 : 0 }}>
            {activeTab === 'configs' && (
              <>
                {configs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <div style={{ fontSize: 44, marginBottom: 12 }}>🎙</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#334155', marginBottom: 6 }}>No Voice Assistants Yet</div>
                    <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 22 }}>
                      Create your first AI phone agent — it will handle calls, answer questions, and capture leads automatically.
                    </div>
                    <button
                      type="button"
                      className="ri-btn ri-btn--primary-solid"
                      onClick={() => setConfigModal({ open: true, mode: 'add', config: null })}
                    >
                      <FeatherIcon icon="plus" size={14} /> Create Voice Assistant
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                    {configs.map((cfg) => (
                      <ConfigCard
                        key={cfg._id}
                        cfg={cfg}
                        onEdit={(c) => setConfigModal({ open: true, mode: 'edit', config: c })}
                        onDelete={(c) => setDeleteModal({ open: true, config: c })}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'leads' && (
              <div>
                <div style={{ padding: '12px 20px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>Filter by assistant:</label>
                  <select
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: 12, outline: 'none', background: '#fff', color: '#0F172A', minWidth: 180 }}
                    value={selectedConfigId}
                    onChange={(e) => setSelectedConfigId(e.target.value)}
                  >
                    <option value="">All Assistants</option>
                    {configs.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.persona?.assistant_name || c.voice_slug}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ padding: '12px 0 0' }}>
                  <VoiceLeadsTable key={refreshKey} configId={selectedConfigId} configs={configs} />
                </div>
              </div>
            )}

            {activeTab === 'sessions' && (
              <div>
                <div style={{ padding: '12px 20px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>Filter by assistant:</label>
                  <select
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: 12, outline: 'none', background: '#fff', color: '#0F172A', minWidth: 180 }}
                    value={selectedConfigId}
                    onChange={(e) => setSelectedConfigId(e.target.value)}
                  >
                    <option value="">All Assistants</option>
                    {configs.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.persona?.assistant_name || c.voice_slug}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ padding: '12px 0 0' }}>
                  <VoiceSessionsTable key={refreshKey} configId={selectedConfigId} configs={configs} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <VoiceAssistantConfigModal
        mode={configModal.mode}
        open={configModal.open}
        onClose={() => setConfigModal({ open: false, mode: 'add', config: null })}
        onSaved={handleSaved}
        existingConfig={configModal.config}
        propertyList={properties}
      />

      <DeleteVoiceAssistantModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, config: null })}
        onDelete={handleDeleteConfirm}
        config={deleteModal.config}
      />
    </div>
  );
};

export { VoiceAssistant };